import { Box, Skeleton } from "@mui/material";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import { NavItemListType, NavItemType } from "types";

import {
  SIDEBAR_RAIL_TILE,
  SIDEBAR_ROW_HEIGHT,
  SidebarGroupSeparator
} from "../MainSidebar.components";
import { MENU_ITEM_ICON_SIZE } from "../const";
import { MainSidebarMenuTree } from "./MainSidebarMenuTree";

const MAX_SKELETON_ROWS = 5;
const REDUCED_PACKAGE_SKELETON_ROWS = 3;
// Groups that render a shorter skeleton (fewer expected rows). "overview" is
// the project sidebar's package group; "manage" and "insights" are three rows
// on both sidebars — the org "manage" group used to be "overview" and silently
// lost its short skeleton when it was renamed.
const REDUCED_SKELETON_GROUP_IDS = new Set(["overview", "manage", "insights"]);
const SKELETON_TEXT_WIDTHS = ["86%", "72%", "78%", "66%", "82%"];
// Group whose collapsed (drawer-closed) state is hidden entirely.
const PROJECTS_GROUP_ID = "projects";
const NAV_GROUP_TYPE = "group";

/**
 * Whether a nav group has anything to render beneath its caption.
 *
 * `MainSidebarMenuTree` already returns null for a childless group, but the
 * caption above it is rendered separately — so a group whose every item was
 * filtered out (the org "View" group on a non-admin account, whose only entry
 * is the admin-gated SROI Builder) printed its heading over blank space.
 *
 * Filtered out of the list entirely rather than skipped inside `MenuList`, for
 * two reasons: an empty group is still a flex child of the sidebar's container
 * and would keep consuming the inter-group gap, and `groupIndex` has to count
 * only the groups that actually render or the collapsed rule lands above the
 * wrong one.
 *
 * Non-group entries pass through untouched — they render their own error row.
 */
export const rendersRows = (item: NavItemType): boolean =>
  item.type !== NAV_GROUP_TYPE || (item.children?.length ?? 0) > 0;

export const getSkeletonRowCount = (item: NavItemType): number =>
  REDUCED_SKELETON_GROUP_IDS.has(item.id)
    ? REDUCED_PACKAGE_SKELETON_ROWS
    : MAX_SKELETON_ROWS;

// Width cycles by (itemIndex + rowIndex) so adjacent skeleton rows differ.
export const getSkeletonTextWidth = (
  itemIndex: number,
  rowIndex: number
): string =>
  SKELETON_TEXT_WIDTHS[(itemIndex + rowIndex) % SKELETON_TEXT_WIDTHS.length];

function MenuList({
  isDrawOpen,
  item,
  selectedItem
}: {
  isDrawOpen: boolean;
  item: NavItemType;
  selectedItem: string;
}) {
  return (
    <>
      {/* What sits above a group's rows. Expanded, a titled group gets its
          caption and an untitled one nothing — that is what makes the shared
          (Level1Only) nav read as one flat list instead of two labelled
          blocks. Collapsed, every group is separated by a short rule, titled
          or not: the rail has no captions, so the rule is the only thing that
          says where one group ends. Both are the same element, so folding
          animates its height rather than swapping a 36px block for a 15px
          one. */}
      <SidebarGroupSeparator open={isDrawOpen} hasTitle={Boolean(item.title)}>
        {item.title}
      </SidebarGroupSeparator>
      <MainSidebarMenuTree
        item={item}
        isDrawOpen={isDrawOpen}
        selectedItem={selectedItem}
      />
    </>
  );
}

const SkeletonGroup = ({
  isDrawOpen,
  item,
  itemIndex
}: {
  isDrawOpen: boolean;
  item: NavItemType;
  itemIndex: number;
}) => {
  const rowCount = getSkeletonRowCount(item);

  return (
    <Box
      sx={{
        alignItems: isDrawOpen ? "stretch" : "center",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Mirrors the separator above exactly — a caption-sized bar where the
          caption
          would be, a rule where the rule would be. Diverging here makes the
          skeleton draw a heading the loaded menu then drops. */}
      {!isDrawOpen || !item.title ? (
        <SidebarGroupSeparator
          open={isDrawOpen}
          hasTitle={Boolean(item.title)}
        />
      ) : (
        <Box sx={{ pb: "6px", pt: "18px", px: "10px" }}>
          <Skeleton
            variant="text"
            height={12}
            width={itemIndex % 2 === 0 ? "40%" : "30%"}
            sx={{ borderRadius: "4px", transform: "none" }}
          />
        </Box>
      )}
      <Box
        sx={{
          alignItems: isDrawOpen ? "stretch" : "center",
          display: "flex",
          flexDirection: "column",
          gap: isDrawOpen ? "2px" : "4px"
        }}
      >
        {Array.from({ length: rowCount }, (_, rowIndex) =>
          isDrawOpen ? (
            <Box
              key={`sidebar-loading-item-${item.id}-${rowIndex}`}
              sx={{
                alignItems: "center",
                display: "flex",
                gap: "10px",
                height: SIDEBAR_ROW_HEIGHT,
                px: "10px"
              }}
            >
              <Skeleton
                variant="circular"
                width={MENU_ITEM_ICON_SIZE}
                height={MENU_ITEM_ICON_SIZE}
              />
              <Skeleton
                variant="text"
                width={getSkeletonTextWidth(itemIndex, rowIndex)}
                height={14}
                sx={{ borderRadius: "4px", transform: "none" }}
              />
            </Box>
          ) : (
            <Skeleton
              key={`sidebar-loading-item-${item.id}-${rowIndex}`}
              variant="rounded"
              width={SIDEBAR_RAIL_TILE}
              height={SIDEBAR_RAIL_TILE}
              sx={{ borderRadius: "10px" }}
            />
          )
        )}
      </Box>
    </Box>
  );
};

export interface Props {
  loading: boolean;
  menuItem: NavItemListType;
  selectedItem: string;
  // Selection is route-driven by the layout; the menu only reads selectedItem.
  setSelectedItem: (item: string) => void;
  isDrawOpen: boolean;
}

const MainSidebarMenu = ({
  isDrawOpen,
  loading,
  menuItem,
  selectedItem,
  setSelectedItem: _setSelectedItem
}: Props) => {
  // One list for both branches: a skeleton group that the loaded menu then drops
  // reads as a fault, which is the same reason the untitled-group rule below is
  // mirrored rather than duplicated loosely.
  const groups = menuItem.items.filter(rendersRows);

  if (loading) {
    return (
      <>
        {groups.map((item, itemIndex) => (
          <SkeletonGroup
            key={`sidebar-loading-group-${item.id}-${itemIndex}`}
            isDrawOpen={isDrawOpen}
            item={item}
            itemIndex={itemIndex}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {groups.map((item) => {
        switch (item.type) {
          case NAV_GROUP_TYPE:
            return (
              <Box
                key={item.id}
                sx={{
                  alignItems: "stretch",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                {item.id === PROJECTS_GROUP_ID && !isDrawOpen ? null : (
                  <MenuList
                    isDrawOpen={isDrawOpen}
                    item={item}
                    selectedItem={selectedItem}
                  />
                )}
              </Box>
            );
          default:
            return (
              <BaseTypography
                key={item.id}
                variant="h6"
                color="error"
                align="center"
              >
                Menu Items Error
              </BaseTypography>
            );
        }
      })}
    </>
  );
};

export default MainSidebarMenu;
